import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { UserPublicData } from "../../types/user";
import { useUserProfile } from "../../stores";

interface UserChipProps {
  userId: string;
  className?: string;
}

const UserChip: React.FC<UserChipProps> = ({ userId, className }) => {
  const { currentUser, authState } = useAuth();
  const navigate = useNavigate();
  
  // Use the individual profile hook with auto-fetching
  const { profile: userData, isLoading: profileLoading } = useUserProfile(
    userId !== currentUser?.uid ? userId : null
  );

  const displayUsername =
    userData?.username ||
    (userId === currentUser?.uid ? authState.user?.username : undefined) ||
    (profileLoading ? "Loading..." : "User");

  const displayImageUrl =
    userData?.imageUrlTiny ||
    userData?.imageUrl ||
    (userId === currentUser?.uid
      ? authState.user?.imageUrlTiny || authState.user?.imageUrl
      : undefined);

  const isLoading = profileLoading || (!userData && userId !== currentUser?.uid);
  const hasError = !userData && !isLoading && userId !== currentUser?.uid;

  // Debug logging for the loading issue
  if (userId !== currentUser?.uid && (isLoading || !userData)) {
    console.log('UserChip debug:', {
      userId,
      currentUserId: currentUser?.uid,
      userData: !!userData,
      profileLoading,
      isLoading,
      hasError,
      displayUsername
    });
  }

  const handleClick = () => {
    // Navigate to profile using userId format (same as Header.tsx)
    navigate(`/profile/${userId}`);
  };

  return (
    <button 
      onClick={handleClick}
      className={`flex items-center gap-2 hover:bg-base-200 rounded-lg p-1 transition-colors ${className || ""}`.trim()}
    >
      <div className={`w-6 h-6 rounded-full overflow-hidden ${hasError ? 'bg-red-200' : isLoading ? 'bg-base-300 animate-pulse' : 'bg-neutral'} flex items-center justify-center`}>
        {displayImageUrl && !isLoading ? (
          <img
            src={displayImageUrl}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
            alt={`${displayUsername} avatar`}
          />
        ) : (
          <div className={`w-full h-full ${hasError ? 'bg-red-300' : isLoading ? 'bg-base-300' : 'bg-neutral'} flex items-center justify-center`}>
            <span className={`text-xs font-semibold font-grotesk ${hasError ? 'text-red-600' : isLoading ? 'text-base-content/60' : 'text-neutral-content'}`}>
              {isLoading ? '...' : displayUsername?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
        )}
      </div>
      <span className={`text-xs font-medium ${hasError ? 'text-red-600' : isLoading ? 'text-base-content/50' : 'text-base-content/80'}`}>
        {displayUsername}
      </span>
    </button>
  );
};

export default UserChip;


